import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';

import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Card } from 'ui-components/components/card';
import { emptyObject } from 'ui-components/default-props/empty';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { TextType, Text } from 'ui-components/components/text';

import ShipFromModal from '../../../../../../../ship-from/modals';
import { trackShipFromEditButtonClick, ShipFromTrackingSources } from '../../../../../../../ship-from/tracking-events';
import useFetchShipFroms from '../../../../../../settings/ship-from-locations/use-fetch-ship-froms';
import { useModal } from '../../../../../../../hooks/use-modal';

import style from './style.module.less';
import messages from '../messages';

/**
 * Builds a formatted address string from address fields, handling missing fields gracefully
 * @param {object} address - The address object
 * @returns {string} Formatted address string
 */
const buildAddressString = (address) => {
  if (!address) return '';

  let displayAddress = address.line1;

  if (address.line2) {
    displayAddress += `, ${address.line2}`;
  }

  const cityStateZip = `${address.city}, ${address.state} ${address.postalCode}`;
  displayAddress += `, ${cityStateZip}`;

  return displayAddress;
};

/**
 * @param {object} props - Component props
 * @param {object} props.shipFrom - Ship-from location data with properties:
 *   shipFromId (string): Unique identifier for the ship-from location,
 *   name (string): Name of the ship-from location,
 *   isDefault (boolean, optional): Whether this is the default ship-from location,
 *   originAddress (object, optional): Address details for the ship-from location
 * @returns {React.ReactElement} A card displaying ship-from location details
 */
export const ShipFromCard = ({ shipFrom }) => {
  const { open, close, isOpen } = useModal();
  const { fetchShipFroms } = useFetchShipFroms();
  const cityStateZip = buildAddressString(shipFrom.originAddress);

  const onSuccess = useCallback(async () => {
    try {
      close();
      await fetchShipFroms();
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.editShipFromErrorMessageTitle} />,
        content: '',
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    }
  }, [fetchShipFroms, close]);

  const handleEditClick = useCallback(() => {
    trackShipFromEditButtonClick(shipFrom.shipFromId, ShipFromTrackingSources.FTU_WIZARD_SHIP_FROM_STEP);
    open();
  }, [shipFrom.shipFromId, open]);

  const { name, shipFromId, isDefault, originAddress = emptyObject } = shipFrom;

  return (
    <Card className={style['ship-from-card']}>
      <div className={style['card-header']}>
        <div className={style['name-container']}>
          <Text className={style['card-title']} textType={TextType.H3} value={name} />
          {isDefault && (
            <span className={style['default-badge']}>
              <Text element='span' textType={TextType.Caption} value={messages.default} />
            </span>
          )}
        </div>
        <Button aria-label='Edit' className={style['edit-button']} onClick={handleEditClick} theme={THEME_CONSTANTS.tertiary}>
          <FontAwesomeIcon className={style['pen-icon']} icon={faPen} />
          <FormattedMessage {...messages.edit} />
        </Button>
      </div>

      <div className={style['card-body']}>
        <Text textType={TextType.Paragraph} value={originAddress.name} />
        <Text textType={TextType.Paragraph} value={originAddress.company} />
        <Text textType={TextType.Paragraph} value={originAddress.country} />
        <Text textType={TextType.Paragraph} value={cityStateZip} />
        {originAddress.email && <Text textType={TextType.Paragraph} value={originAddress.email} />}
        <Text textType={TextType.Paragraph} value={originAddress.phone} />
      </div>

      {isOpen && (
        <ShipFromModal
          onClose={close}
          onSuccess={onSuccess}
          shipFromId={shipFromId}
          shouldCreateInventoryWarehouse
          source={ShipFromTrackingSources.FTU_WIZARD_SHIP_FROM_STEP}
        />
      )}
    </Card>
  );
};
